import { useEffect, useRef, useState } from 'react';
import { Layers } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20000;

function xhrJson(url, headers) {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    Object.entries(headers || {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.timeout = 15000;
    xhr.onload = () => {
      try { resolve(xhr.status >= 200 && xhr.status < 300 ? JSON.parse(xhr.responseText || '{}') : null); }
      catch { resolve(null); }
    };
    xhr.onerror = () => resolve(null);
    xhr.ontimeout = () => resolve(null);
    xhr.send(null);
  });
}

// Small pill that shows queue position + live remaining hours for a specific
// project. Polls /api/production-queue/me every 20s so other clients starting
// projects in parallel shift this project's position/ETA in near-realtime.
export default function QueueStatusPill({ projectId }) {
  const { session } = useAuth();
  const token = session?.access_token;
  const [row, setRow] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (!token || !projectId) return;
    const headers = { Authorization: `Bearer ${token}` };
    let stopped = false;
    const tick = async () => {
      const data = await xhrJson(`${API}/api/production-queue/me?_=${Date.now()}`, headers);
      if (stopped || !mountedRef.current) return;
      const match = (data?.queue || []).find((r) => r.project_id === projectId);
      setRow(match || null);
    };
    tick();
    const interval = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token, projectId]);

  // Tick once a second so the "remaining hours" text feels alive.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000); // 1-min resolution is enough
    return () => clearInterval(t);
  }, []);

  if (!row) return null;

  const deliveryMs = row.estimated_delivery_at ? new Date(row.estimated_delivery_at).getTime() : 0;
  const remainingMs = Math.max(0, deliveryMs - now);
  const remainingHrs = remainingMs / 3_600_000;
  const hoursLabel =
    remainingHrs >= 1
      ? `${remainingHrs >= 10 ? Math.round(remainingHrs) : remainingHrs.toFixed(1)}h`
      : `${Math.max(1, Math.round(remainingMs / 60_000))}m`;

  const inProgress = row.status === 'in_progress';

  return (
    <div
      data-testid="queue-status-pill"
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-gray-200/70 backdrop-blur-sm shadow-[0_4px_14px_-4px_rgba(0,0,0,0.08)] text-[12px]"
    >
      <Layers className={`w-3.5 h-3.5 ${inProgress ? 'text-emerald-500' : 'text-gray-400'}`} />
      {inProgress ? (
        <span className="font-medium text-gray-700">En producción · entrega en ~{hoursLabel}</span>
      ) : (
        <>
          <span className="font-medium text-gray-700">
            You are #{row.position} in the queue
          </span>
          <span className="text-gray-400">·</span>
          <span className="text-gray-500">Estimated delivery: {hoursLabel}</span>
        </>
      )}
    </div>
  );
}
